"use client";

import type { FC, KeyboardEvent, ReactNode } from "react";
import { useMemo, useState } from "react";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import * as RadixPopover from "@radix-ui/react-popover";
import { Checkbox } from "@/components/checkbox";
import { Tag } from "@/components/tag";
import { cn } from "@/lib/cn";
import { FieldLabel, FieldMessage, POPOVER_PANEL_WIDTH, type FieldState } from "./field-chrome";
import {
  isLockedParent,
  toggleTreeValue,
  type TreeOption,
  withImpliedParents,
  withoutImpliedParents,
} from "./tree-selection";

export { toTreeOptions, type TreeOption } from "./tree-selection";

type TreeMultiComboboxProps = {
  className?: string;
  containerClassName?: string;
  label?: ReactNode;
  message?: ReactNode;
  state?: FieldState;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  options: TreeOption[];
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (values: string[]) => void;
  /** Whether `value` carries the parents a ticked child implies; they show ticked either way. Off
   *  for filters, where a parent matches everything under it and would widen the search. */
  keepImpliedParents?: boolean;
  maxVisibleTags?: number;
  id?: string;
};

type Row = { option: TreeOption; depth: 0 | 1 };

const DEFAULT_MAX_VISIBLE_TAGS = 6;

const matches = (text: string, query: string) => text.toLowerCase().includes(query.toLowerCase());

/** Rows in tree order. Searching keeps a parent whenever it or any of its children matches. */
const rowsOf = (options: TreeOption[], search: string): Row[] =>
  options.flatMap((option) => {
    const children = option.children ?? [];
    const shownChildren =
      !search.trim() || matches(option.label, search)
        ? children
        : children.filter((child) => matches(child.label, search));

    if (search.trim() && !matches(option.label, search) && shownChildren.length === 0) {
      return [];
    }

    return [
      { option, depth: 0 as const },
      ...shownChildren.map((child) => ({ option: child, depth: 1 as const })),
    ];
  });

/**
 * Multi-select over a two-level tree where a parent is a choice too, not just a heading: ticking a
 * child ticks its parent, which stays locked until its last child goes. Tags show what was picked,
 * leaving out the parents a ticked child already implies.
 */
export const TreeMultiCombobox: FC<TreeMultiComboboxProps> = ({
  className,
  containerClassName,
  label,
  message,
  state = "default",
  required,
  disabled,
  placeholder = "Please select",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  options,
  value,
  defaultValue = [],
  onValueChange,
  keepImpliedParents = true,
  maxVisibleTags = DEFAULT_MAX_VISIBLE_TAGS,
  id,
}) => {
  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState<string[]>(defaultValue);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const stored = value ?? internal;
  const selected = keepImpliedParents ? stored : withImpliedParents(options, stored);

  const rows = useMemo(() => rowsOf(options, search), [options, search]);
  const allOptions = useMemo(() => rowsOf(options, "").map((row) => row.option), [options]);

  const shown = withoutImpliedParents(options, selected);
  const tagged = allOptions.filter((option) => shown.includes(option.value));
  const visible = maxVisibleTags > 0 ? tagged.slice(0, maxVisibleTags) : tagged;
  const hidden = tagged.length - visible.length;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setSearch("");
      setHighlightedIndex(0);
    }
  };

  const toggle = (optionValue: string) => {
    const next = toggleTreeValue(options, selected, optionValue);
    if (next === selected) return;

    const emitted = keepImpliedParents ? next : withoutImpliedParents(options, next);
    setInternal(emitted);
    onValueChange?.(emitted);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((index) => Math.min(index + 1, rows.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((index) => Math.max(index - 1, 0));
        break;
      case "Enter": {
        const row = rows[highlightedIndex];
        if (row) {
          event.preventDefault();
          toggle(row.option.value);
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className={cn("flex w-full flex-col gap-8", containerClassName)}>
      <FieldLabel htmlFor={id} state={state} required={required}>
        {label}
      </FieldLabel>
      <RadixPopover.Root
        /* `modal` keeps the wheel on the option list, as in NestedMultiCombobox. */
        modal
        open={open}
        onOpenChange={handleOpenChange}
      >
        <RadixPopover.Trigger
          id={id}
          disabled={disabled}
          className={cn(
            "flex min-h-62 w-full cursor-pointer items-center justify-between gap-8 rounded-medium border bg-neutral-white p-16 text-left outline-none focus:border-brand-blue",
            "disabled:cursor-not-allowed disabled:bg-neutral-black/5",
            state === "error" ? "border-brand-red" : "border-neutral-mid-grey",
            className,
          )}
        >
          {selected.length === 0 ? (
            <span className="text-16 text-neutral-black/35">{placeholder}</span>
          ) : (
            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-8">
              {visible.map((option) => (
                <Tag
                  key={option.value}
                  size="s"
                  className="max-w-full"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggle(option.value);
                  }}
                >
                  <span className="truncate">{option.label}</span>
                </Tag>
              ))}
              {hidden > 0 && (
                <Tag size="s" iconRight={false} className="text-neutral-black/70">
                  +{hidden}
                </Tag>
              )}
            </span>
          )}
          <ChevronDownIcon className="size-24 shrink-0 text-neutral-black" />
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content
            align="start"
            side="bottom"
            sideOffset={8}
            collisionPadding={16}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              (event.currentTarget as HTMLElement).querySelector("input")?.focus();
            }}
            className={cn(
              "pointer-events-auto z-50 flex flex-col gap-4 rounded-small border border-neutral-light-grey bg-neutral-white p-8 shadow-lg data-[state=closed]:animate-[dropdown-out_150ms_ease-in] data-[state=open]:animate-[dropdown-in_150ms_ease-out]",
              POPOVER_PANEL_WIDTH,
            )}
          >
            <div className="flex items-center gap-8 rounded-small border border-neutral-mid-grey bg-neutral-white px-12 py-8">
              <MagnifyingGlassIcon className="size-20 shrink-0 text-neutral-black/35" aria-hidden />
              <input
                autoFocus
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setHighlightedIndex(0);
                }}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="min-w-0 flex-1 bg-transparent text-16 text-neutral-black outline-none placeholder:text-neutral-black/35"
              />
            </div>

            {rows.length === 0 ? (
              <p className="px-8 py-12 text-14 text-neutral-grey">{emptyText}</p>
            ) : (
              <div className="flex max-h-280 flex-col gap-4 overflow-y-auto">
                {rows.map(({ option, depth }, index) => {
                  const isSelected = selected.includes(option.value);
                  const locked = isLockedParent(options, selected, option.value);

                  return (
                    <label
                      key={option.value}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      title={locked ? "Untick its options below to remove this" : undefined}
                      className={cn(
                        "flex items-center gap-8 rounded-small py-8 pr-8 transition-colors duration-100",
                        depth === 1 ? "pl-32" : "pl-8",
                        locked ? "cursor-not-allowed" : "cursor-pointer",
                        index === highlightedIndex && "bg-tone-aqua-tint",
                      )}
                    >
                      <Checkbox
                        size="m"
                        checked={isSelected}
                        disabled={locked}
                        onCheckedChange={() => toggle(option.value)}
                      />
                      <span
                        className={cn(
                          "min-w-0 text-16 text-neutral-black",
                          depth === 0 && option.children?.length && "font-semibold",
                        )}
                      >
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
      <FieldMessage state={state}>{message}</FieldMessage>
    </div>
  );
};
